import React, { useState, useEffect } from 'react'
import { fieldMappingService } from '../services/fieldMappingService'
import FieldMappingsSideNav from './FieldMappingsSideNav'
import TestMappingsModal from './TestMappingsModal'
import { VALIDATORS } from '../../validators'
import type { 
  ApplicationFieldMapping, 
  SalesforceFieldMetadata, 
  ApplicationFieldMappingTestResult,
  CreateApplicationFieldMappingData
} from '../types'
import '../styles/FieldMappings.css'

interface SalesforceFieldsCache {
  Account: SalesforceFieldMetadata[]
  Opportunity: SalesforceFieldMetadata[]
  Contact: SalesforceFieldMetadata[]
}

const FieldMappingsManager: React.FC = () => {
  // State management
  const [mappings, setMappings] = useState<ApplicationFieldMapping[]>([])
  const [salesforceFields, setSalesforceFields] = useState<SalesforceFieldsCache>({
    Account: [],
    Opportunity: [],
    Contact: []
  })
  const [categories, setCategories] = useState<string[]>([])
  const [activeCategory, setActiveCategory] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showTestResults, setShowTestResults] = useState(false)
  const [testResults, setTestResults] = useState<ApplicationFieldMappingTestResult | null>(null)
  
  // System Configuration state
  const [accountLinkField, setAccountLinkField] = useState<string | null>(null)
  const [accountLinkFieldConfigured, setAccountLinkFieldConfigured] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  
  // Sync state
  const [syncing, setSyncing] = useState(false)
  
  // Opportunity fields with reference fields (for Account Link Field configuration)
  const [opportunityFieldsWithReference, setOpportunityFieldsWithReference] = useState<SalesforceFieldMetadata[]>([])
  
    // Opportunity defaults configuration state
  const [opportunityNameTemplate, setOpportunityNameTemplate] = useState<string>('')
  const [opportunityDefaultStage, setOpportunityDefaultStage] = useState<string>('')
  const [opportunityCloseDays, setOpportunityCloseDays] = useState<number>(30)
  const [opportunityCloseDateTemplate, setOpportunityCloseDateTemplate] = useState<string>('')
  const [templatePlaceholders, setTemplatePlaceholders] = useState<{ [key: string]: string }>({})
  const [savingOpportunityConfig, setSavingOpportunityConfig] = useState(false)
  const [stagePicklistValues, setStagePicklistValues] = useState<Array<{ label: string; value: string }>>([])  
  const [loadingStageValues, setLoadingStageValues] = useState(false)
  
  // Opportunity Owner configuration state
  const [opportunityOwnerFieldName, setOpportunityOwnerFieldName] = useState<string>('')
  const [opportunityOwnerType, setOpportunityOwnerType] = useState<'dynamic' | 'static'>('dynamic')
  const [opportunityOwnerStaticValue, setOpportunityOwnerStaticValue] = useState<string>('')
  const [salesforceUsers, setSalesforceUsers] = useState<Array<{ id: string; name: string; email: string }>>([])
  const [loadingSalesforceUsers, setLoadingSalesforceUsers] = useState(false)
  const [savingOwnerConfig, setSavingOwnerConfig] = useState(false)
  const [opportunityReferenceFields, setOpportunityReferenceFields] = useState<SalesforceFieldMetadata[]>([])
  
  // Track original values to detect changes
  const [originalOpportunityNameTemplate, setOriginalOpportunityNameTemplate] = useState<string>('')
  const [originalOpportunityCloseDateTemplate, setOriginalOpportunityCloseDateTemplate] = useState<string>('')

  // Available Salesforce objects
  const salesforceObjects = [
    { value: 'Account', label: 'Account' },
    { value: 'Opportunity', label: 'Opportunity' },
    { value: 'Contact', label: 'Contact' }
  ]

  // Available validators from the shared validators registry
  const availableValidators = Object.keys(VALIDATORS).map(key => ({
    id: key,
    name: VALIDATORS[key].name || key,
    description: VALIDATORS[key].message
  }))

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)

        console.log('🔄 Loading field mapping data...')

        // Load categories first
        const categoriesData = await fieldMappingService.getCategories()
        
        // Add General tab to the beginning and set it as default
        const allCategories = ['General', ...categoriesData]
        setCategories(allCategories)
        setActiveCategory('General')

        console.log('✅ Categories loaded:', categoriesData)

        // Load existing field mappings
        const mappingsData = await fieldMappingService.getAllMappings()
        setMappings(mappingsData)

        console.log('✅ Field mappings loaded:', mappingsData.length)

        // Preload Salesforce fields for all objects
        console.log('🔄 Loading Salesforce object fields...')
        
        const fieldPromises = salesforceObjects.map(async (obj) => {
          try {
            const fields = await fieldMappingService.getSalesforceObjectFields(obj.value as any)
            console.log(`✅ Loaded ${fields.length} ${obj.value} fields with types`)
            return { objectName: obj.value, fields }
          } catch (err) {
            console.error(`❌ Error loading fields for ${obj.value}:`, err)
            return { objectName: obj.value, fields: [] }
          }
        })

        const results = await Promise.all(fieldPromises)
        
        const allFields = results.reduce((acc, result) => {
          acc[result.objectName as keyof SalesforceFieldsCache] = result.fields
          return acc
        }, {} as SalesforceFieldsCache)

        setSalesforceFields(allFields)
        console.log('✅ All Salesforce fields loaded')

        // Load Opportunity fields with reference fields for Account Link Field configuration
        console.log('🔄 Loading Opportunity fields with reference fields...')
        try {
          const opportunityFieldsWithRef = await fieldMappingService.getSalesforceObjectFields('Opportunity', true)
          setOpportunityFieldsWithReference(opportunityFieldsWithRef)
          
          // Filter reference fields for owner configuration (User references)
          const referenceFields = opportunityFieldsWithRef.filter(field => 
            field.type === 'reference' && 
            (field.name.toLowerCase().includes('owner') || field.name === 'OwnerId' || field.referenceTo?.includes('User'))
          )
          setOpportunityReferenceFields(referenceFields)
          
          console.log(`✅ Loaded ${opportunityFieldsWithRef.length} Opportunity fields with reference fields`)
          console.log(`✅ Found ${referenceFields.length} potential owner reference fields`)
        } catch (err) {
          console.error('❌ Error loading Opportunity fields with reference:', err)
        }

        // Load system configuration
        console.log('🔄 Loading system configuration...')
        try {
          const configResult = await fieldMappingService.getAccountLinkField()
          setAccountLinkField(configResult.fieldName)
          setAccountLinkFieldConfigured(configResult.configured)
          console.log('✅ System configuration loaded:', configResult)
        } catch (configError) {
          console.warn('⚠️ Could not load system configuration:', configError)
        }

        // Load opportunity defaults configuration
        console.log('🔄 Loading opportunity defaults configuration...')
        try {
          const [nameTemplate, defaultStage, closeDays, closeDateTemplate, placeholders, stagePicklist, ownerConfig] = await Promise.all([
            fieldMappingService.getOpportunityNameTemplate(),
            fieldMappingService.getOpportunityDefaultStage(),
            fieldMappingService.getOpportunityCloseDays(),
            fieldMappingService.getOpportunityCloseDateTemplate(),
            fieldMappingService.getTemplatePlaceholders(),
            fieldMappingService.getOpportunityStagePicklistValues(),
            fieldMappingService.getOpportunityOwnerConfig().catch(() => ({ fieldName: '', type: 'dynamic', staticValue: '' }))
          ])
          
          setOpportunityNameTemplate(nameTemplate)
          setOriginalOpportunityNameTemplate(nameTemplate)
          setOpportunityDefaultStage(defaultStage)
          setOpportunityCloseDays(closeDays)
          setOpportunityCloseDateTemplate(closeDateTemplate)
          setOriginalOpportunityCloseDateTemplate(closeDateTemplate)
          setTemplatePlaceholders(placeholders)
          setStagePicklistValues(stagePicklist)
          setOpportunityOwnerFieldName(ownerConfig.fieldName || '')
          setOpportunityOwnerType(ownerConfig.type as 'dynamic' | 'static')
          setOpportunityOwnerStaticValue(ownerConfig.staticValue || '')
          
          console.log('✅ Opportunity defaults configuration loaded:', {
            nameTemplate, defaultStage, closeDays, closeDateTemplate, stageCount: stagePicklist.length
          })
        } catch (opportunityConfigError) {
          console.warn('⚠️ Could not load opportunity defaults configuration:', opportunityConfigError)
        }

        // Auto-populate missing field types for existing mappings
        const updatedMappings = mappingsData.map(mapping => {
          if (mapping.salesforceField && !mapping.salesforceFieldType) {
            const objectFields = allFields[mapping.salesforceObject]
            const sfField = objectFields?.find(field => field.name === mapping.salesforceField)
            if (sfField) {
              console.log(`🔄 Auto-setting type for ${mapping.formFieldName}: ${sfField.type}`)
              return { ...mapping, salesforceFieldType: sfField.type }
            }
          }
          return mapping
        })
        
        setMappings(updatedMappings)
        
        // Only auto-save if there were actual changes to field types
        const hasFieldTypeChanges = updatedMappings.some((mapping, index) => {
          const original = mappingsData[index]
          return original && (
            mapping.salesforceFieldType !== original.salesforceFieldType ||
            mapping.salesforceFieldLabel !== original.salesforceFieldLabel ||
            mapping.salesforcePicklistOptions !== original.salesforcePicklistOptions ||
            mapping.salesforceMaxLength !== original.salesforceMaxLength
          )
        })
        
        if (hasFieldTypeChanges) {
          try {
            console.log('💾 Saving auto-populated field types...')
            
            // Deduplicate mappings by formFieldName before saving
            const uniqueMappings = updatedMappings.reduce((acc, mapping) => {
              const existing = acc.find(m => m.formFieldName === mapping.formFieldName)
              if (!existing) {
                acc.push(mapping)
              } else {
                console.warn(`⚠️ Duplicate formFieldName found: ${mapping.formFieldName}, skipping duplicate`)
              }
              return acc
            }, [] as typeof updatedMappings)
            
            await fieldMappingService.saveMappings(uniqueMappings.map(mapping => ({
              formFieldName: mapping.formFieldName,
              formFieldLabel: mapping.formFieldLabel,
              formFieldType: mapping.formFieldType,
              formFieldCategory: mapping.formFieldCategory,
              salesforceObject: mapping.salesforceObject,
              salesforceField: mapping.salesforceField,
              isRequired: mapping.isRequired,
              displayOrder: mapping.displayOrder || 0,
              placeholder: mapping.placeholder,
              helpText: mapping.helpText,
              validationRules: mapping.validationRules,
              isActive: mapping.isActive,
              salesforceFieldType: mapping.salesforceFieldType,
              salesforceFieldLabel: mapping.salesforceFieldLabel,
              salesforcePicklistOptions: mapping.salesforcePicklistOptions,
              salesforceMaxLength: mapping.salesforceMaxLength,
              fieldMappingValidated: mapping.fieldMappingValidated,
              lastValidatedAt: mapping.lastValidatedAt
            })))
            console.log('✅ Field types auto-saved successfully')
          } catch (saveError) {
            console.warn('⚠️ Could not auto-save field types:', saveError)
          }
        } else {
          console.log('ℹ️ No field type changes detected, skipping auto-save')
        }

        setLoading(false)
      } catch (err) {
        console.error('❌ Error loading field mapping data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load data')
        setLoading(false)
      }
    }

    loadData()
  }, [])

  // Handle field changes
  const handleMappingChange = (formFieldName: string, field: keyof ApplicationFieldMapping, value: any) => {
    setMappings(prev => prev.map(mapping => 
      mapping.formFieldName === formFieldName 
        ? { ...mapping, [field]: value }
        : mapping
    ))
  }

  // Handle validator selection changes
  const handleValidatorChange = (formFieldName: string, validatorId: string, enabled: boolean) => {
    setMappings(prev => prev.map(mapping => {
      if (mapping.formFieldName === formFieldName) {
        const currentValidators = mapping.validationRules?.validators || []
        
        let updatedValidators
        if (enabled) {
          // Add validator if not already present
          if (!currentValidators.includes(validatorId)) {
            updatedValidators = [...currentValidators, validatorId]
          } else {
            updatedValidators = currentValidators
          }
        } else {
          // Remove validator
          updatedValidators = currentValidators.filter(id => id !== validatorId)
        }
        
        const updatedMapping = {
          ...mapping,
          validationRules: {
            ...mapping.validationRules,
            validators: updatedValidators
          }
        }
        
        // Auto-save the change
        autoSaveMapping(formFieldName, 'validationRules', updatedMapping.validationRules, mapping)
        
        return updatedMapping
      }
      return mapping
    }))
  }

  // Auto-save a single mapping change to database
  const autoSaveMapping = async (formFieldName: string, field: string, value: any, mapping: ApplicationFieldMapping) => {
    try {
      console.log(`💾 Auto-saving ${formFieldName}.${field}:`, value)
      
      // Update the mapping with the new value
      const updatedMapping = { ...mapping, [field]: value }
      
      // If we're changing the salesforce field, also update the field type
      if (field === 'salesforceField' && value && mapping.salesforceObject) {
        const objectFields = salesforceFields[mapping.salesforceObject as keyof SalesforceFieldsCache]
        const sfField = objectFields?.find(f => f.name === value)
        if (sfField) {
          updatedMapping.salesforceFieldType = sfField.type
          updatedMapping.salesforceFieldLabel = sfField.label
          updatedMapping.salesforcePicklistOptions = sfField.picklistValues
          updatedMapping.salesforceMaxLength = sfField.maxLength
          updatedMapping.fieldMappingValidated = true
          updatedMapping.lastValidatedAt = new Date().toISOString()
          
          console.log(`🔄 Auto-setting metadata for ${formFieldName}:`, {
            type: sfField.type,
            label: sfField.label,
            picklistCount: sfField.picklistValues?.length || 0
          })
        }
      }
      
      // Update local state
      setMappings(prev => prev.map(m => 
        m.formFieldName === formFieldName ? updatedMapping : m
      ))
      
      // Save to database
      const allMappings = mappings.map(m => 
        m.formFieldName === formFieldName ? updatedMapping : m
      )
      
      await fieldMappingService.saveMappings(allMappings.map(mapping => ({
        formFieldName: mapping.formFieldName,
        formFieldLabel: mapping.formFieldLabel,
        formFieldType: mapping.formFieldType,
        formFieldCategory: mapping.formFieldCategory,
        salesforceObject: mapping.salesforceObject,
        salesforceField: mapping.salesforceField,
        isRequired: mapping.isRequired,
        displayOrder: mapping.displayOrder || 0,
        placeholder: mapping.placeholder,
        helpText: mapping.helpText,
        validationRules: mapping.validationRules,
        isActive: mapping.isActive,
        salesforceFieldType: mapping.salesforceFieldType,
        salesforceFieldLabel: mapping.salesforceFieldLabel,
        salesforcePicklistOptions: mapping.salesforcePicklistOptions,
        salesforceMaxLength: mapping.salesforceMaxLength,
        fieldMappingValidated: mapping.fieldMappingValidated,
        lastValidatedAt: mapping.lastValidatedAt
      })))
      
      console.log(`✅ Auto-saved ${formFieldName}`)
    } catch (error) {
      console.error(`❌ Error auto-saving ${formFieldName}:`, error)
    }
  }

  // Handle account link field configuration save
  const handleAccountLinkFieldSave = async (fieldName: string) => {
    try {
      setSavingConfig(true)
      setError(null)
      setSuccess(null)

      console.log('💾 Saving account link field configuration:', fieldName)

      const result = await fieldMappingService.setAccountLinkField(fieldName)
      
      setAccountLinkField(result.fieldName)
      setAccountLinkFieldConfigured(true)
      setSuccess('Account link field configuration saved successfully!')
      
      setTimeout(() => setSuccess(null), 3000)
      
      console.log('✅ Account link field configuration saved:', result)
    } catch (err) {
      console.error('❌ Error saving account link field configuration:', err)
      setError(err instanceof Error ? err.message : 'Failed to save configuration')
    } finally {
      setSavingConfig(false)
    }
  }

  // Load Salesforce users for owner configuration
  const loadSalesforceUsers = async () => {
    try {
      setLoadingSalesforceUsers(true)
      console.log('👥 Loading Salesforce users for owner configuration...')
      
      const response = await fetch('/api/admin/invitations/salesforce-users', {
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        setSalesforceUsers(data.users || [])
        console.log(`✅ Loaded ${data.users?.length || 0} Salesforce users`)
      } else {
        console.error('❌ Failed to load Salesforce users')
        setSalesforceUsers([])
      }
    } catch (error) {
      console.error('❌ Error loading Salesforce users:', error)
      setSalesforceUsers([])
    } finally {
      setLoadingSalesforceUsers(false)
    }
  }

  // Handle opportunity owner configuration save
  const handleOpportunityOwnerConfigSave = async (fieldName?: string, type?: 'dynamic' | 'static', staticValue?: string) => {
    try {
      setSavingOwnerConfig(true)
      setError(null)
      setSuccess(null)

      const config = {
        fieldName: fieldName !== undefined ? fieldName : opportunityOwnerFieldName,
        type: type !== undefined ? type : opportunityOwnerType,
        staticValue: staticValue !== undefined ? staticValue : opportunityOwnerStaticValue
      }

      console.log('💾 Saving opportunity owner configuration:', config)

      await fieldMappingService.setOpportunityOwnerConfig(config)
      
      if (fieldName !== undefined) setOpportunityOwnerFieldName(fieldName)
      if (type !== undefined) setOpportunityOwnerType(type)
      if (staticValue !== undefined) setOpportunityOwnerStaticValue(staticValue)
      
      setSuccess('Opportunity owner configuration saved successfully!')
      setTimeout(() => setSuccess(null), 3000)
      
      console.log('✅ Opportunity owner configuration saved')
    } catch (err) {
      console.error('❌ Error saving opportunity owner configuration:', err)
      setError(err instanceof Error ? err.message : 'Failed to save owner configuration')
    } finally {
      setSavingOwnerConfig(false)
    }
  }

  // Handle opportunity configuration save
  const handleOpportunityConfigSave = async (field: string, value: string | number) => {
    try {
      setSavingOpportunityConfig(true)
      setError(null)
      setSuccess(null)

      console.log(`💾 Saving opportunity ${field}:`, value)
      console.log('🔍 Current state:', {
        opportunityNameTemplate,
        opportunityDefaultStage,
        opportunityCloseDays,
        opportunityCloseDateTemplate
      })

      switch (field) {
        case 'nameTemplate':
          await fieldMappingService.setOpportunityNameTemplate(value as string)
          setOpportunityNameTemplate(value as string)
          setOriginalOpportunityNameTemplate(value as string)
          break
        case 'defaultStage':
          await fieldMappingService.setOpportunityDefaultStage(value as string)
          setOpportunityDefaultStage(value as string)
          break
        case 'closeDays':
          await fieldMappingService.setOpportunityCloseDays(value as number)
          setOpportunityCloseDays(value as number)
          break
        case 'closeDateTemplate':
          await fieldMappingService.setOpportunityCloseDateTemplate(value as string)
          setOpportunityCloseDateTemplate(value as string)
          setOriginalOpportunityCloseDateTemplate(value as string)
          break
      }
      
      setSuccess(`Opportunity ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} updated successfully!`)
      setTimeout(() => setSuccess(null), 3000)
      
      console.log(`✅ Opportunity ${field} saved:`, value)
    } catch (err) {
      console.error(`❌ Error saving opportunity ${field}:`, err)
      setError(err instanceof Error ? err.message : `Failed to save ${field}`)
    } finally {
      setSavingOpportunityConfig(false)
    }
  }

  // Handle Salesforce field sync
  const handleSyncSalesforceFields = async () => {
    try {
      setSyncing(true)
      setError(null)
      setSuccess(null)

      console.log('🔄 Starting Salesforce field sync...')

      const result = await fieldMappingService.syncSalesforceFields()
      
      console.log('✅ Salesforce field sync completed:', result)
      
      // Show success message with details
      setSuccess(
        `Salesforce fields synced successfully! ` +
        `Updated ${result.syncedObjects} objects, ${result.totalFields} fields, ` +
        `and validated ${result.validatedMappings} mappings.`
      )
      
      // Refresh Salesforce fields data
      console.log('🔄 Refreshing Salesforce fields data...')
      const fieldPromises = salesforceObjects.map(async (obj) => {
        try {
          const fields = await fieldMappingService.getSalesforceObjectFields(obj.value as any)
          console.log(`✅ Refreshed ${fields.length} ${obj.value} fields`)
          return { objectName: obj.value, fields }
        } catch (err) {
          console.error(`❌ Error refreshing fields for ${obj.value}:`, err)
          return { objectName: obj.value, fields: salesforceFields[obj.value as keyof SalesforceFieldsCache] || [] }
        }
      })

      const refreshResults = await Promise.all(fieldPromises)
      
      const refreshedFields = refreshResults.reduce((acc, result) => {
        acc[result.objectName as keyof SalesforceFieldsCache] = result.fields
        return acc
      }, {} as SalesforceFieldsCache)

      setSalesforceFields(refreshedFields)
      console.log('✅ Salesforce fields data refreshed')
      
      // Also refresh Opportunity fields with reference for Account Link Field
      try {
        const opportunityFieldsWithRef = await fieldMappingService.getSalesforceObjectFields('Opportunity', true)
        setOpportunityFieldsWithReference(opportunityFieldsWithRef)
        console.log(`✅ Refreshed ${opportunityFieldsWithRef.length} Opportunity fields with reference`)
      } catch (err) {
        console.error('❌ Error refreshing Opportunity fields with reference:', err)
      }
      
      // Refresh stage picklist values
      try {
        const refreshedStagePicklist = await fieldMappingService.getOpportunityStagePicklistValues()
        setStagePicklistValues(refreshedStagePicklist)
        console.log(`✅ Refreshed ${refreshedStagePicklist.length} stage picklist values`)
      } catch (err) {
        console.error('❌ Error refreshing stage picklist values:', err)
      }
      
      setTimeout(() => setSuccess(null), 5000)
    } catch (err) {
      console.error('❌ Error syncing Salesforce fields:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync Salesforce fields')
    } finally {
      setSyncing(false)
    }
  }

  // Handle save mappings
  const handleSaveMappings = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Filter out invalid mappings
      const validMappings: CreateApplicationFieldMappingData[] = mappings
        .filter(mapping => mapping.formFieldName && mapping.salesforceField && mapping.salesforceObject)
        .map(mapping => ({
          formFieldName: mapping.formFieldName,
          formFieldLabel: mapping.formFieldLabel,
          formFieldType: mapping.formFieldType,
          formFieldCategory: mapping.formFieldCategory,
          salesforceObject: mapping.salesforceObject,
          salesforceField: mapping.salesforceField,
          isRequired: mapping.isRequired,
          displayOrder: mapping.displayOrder,
          placeholder: mapping.placeholder,
          helpText: mapping.helpText,
          validationRules: mapping.validationRules,
          isActive: mapping.isActive
        }))

      console.log('💾 Saving field mappings:', validMappings.length)

      await fieldMappingService.saveMappings(validMappings)
      
      setSuccess('Field mappings saved successfully!')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('❌ Error saving field mappings:', err)
      setError(err instanceof Error ? err.message : 'Failed to save mappings')
    } finally {
      setSaving(false)
    }
  }

  // Handle test mappings
  const handleTest = async () => {
    try {
      setTesting(true)
      setError(null)
      setSuccess(null)

      const validMappings = mappings.filter(mapping => 
        mapping.formFieldName && mapping.salesforceField && mapping.salesforceObject
      )

      if (validMappings.length === 0) {
        setError('No valid mappings to test. Please configure at least one field mapping.')
        return
      }

      console.log('🧪 Testing field mappings:', validMappings.length)

      const result = await fieldMappingService.testMappings(validMappings)
      setTestResults(result)
      setShowTestResults(true)
    } catch (err) {
      console.error('❌ Error testing field mappings:', err)
      setError(err instanceof Error ? err.message : 'Failed to test mappings')
    } finally {
      setTesting(false)
    }
  }

  // Get filtered mappings for current category
  const getFilteredMappings = (): ApplicationFieldMapping[] => {
    return mappings.filter(mapping => {
      const matchesCategory = mapping.formFieldCategory === activeCategory
      const matchesSearch = !searchTerm || 
        mapping.formFieldLabel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.formFieldName.toLowerCase().includes(searchTerm.toLowerCase())
      
      return matchesCategory && matchesSearch
    })
  }

  // Get real-time Salesforce field metadata
  const getSalesforceFieldMetadata = (salesforceObject: string, salesforceField: string) => {
    if (!salesforceField || !salesforceObject) return null
    
    const objectFields = salesforceFields[salesforceObject as keyof SalesforceFieldsCache]
    if (!objectFields || objectFields.length === 0) return null
    
    return objectFields.find(field => field.name === salesforceField) || null
  }

  // Calculate mapping stats for sidebar
  const calculateMappingStats = () => {
    const stats: { [category: string]: { mapped: number; total: number } } = {}
    
    categories.forEach(category => {
      if (category === 'General') {
        // For General tab, show configuration status
        const isConfigured = accountLinkFieldConfigured && opportunityDefaultStage && opportunityNameTemplate
        stats[category] = {
          mapped: isConfigured ? 1 : 0,
          total: 1
        }
      } else {
        const categoryMappings = mappings.filter(mapping => mapping.formFieldCategory === category)
        const mappedCount = categoryMappings.filter(mapping => 
          mapping.salesforceField && mapping.salesforceObject
        ).length
        
        stats[category] = {
          mapped: mappedCount,
          total: categoryMappings.length
        }
      }
    })
    
    return stats
  }

  // Save all General tab configurations
  const handleSaveGeneralConfig = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      console.log('💾 Saving all General tab configurations...')

      // Save all current configuration values, regardless of whether they changed
      const savePromises = []
      
      // Save Opportunity Name Template if it has a value
      if (opportunityNameTemplate.trim()) {
        savePromises.push(
          fieldMappingService.setOpportunityNameTemplate(opportunityNameTemplate).then(() => {
            setOriginalOpportunityNameTemplate(opportunityNameTemplate)
            console.log('✅ Opportunity name template saved')
          })
        )
      }
      
      // Save Default Stage if it has a value
      if (opportunityDefaultStage.trim()) {
        savePromises.push(
          fieldMappingService.setOpportunityDefaultStage(opportunityDefaultStage).then(() => {
            console.log('✅ Opportunity default stage saved')
          })
        )
      }
      
      // Save Close Days (always has a value)
      savePromises.push(
        fieldMappingService.setOpportunityCloseDays(opportunityCloseDays).then(() => {
          console.log('✅ Opportunity close days saved')
        })
      )
      
      // Save Close Date Template if it has a value
      if (opportunityCloseDateTemplate.trim()) {
        savePromises.push(
          fieldMappingService.setOpportunityCloseDateTemplate(opportunityCloseDateTemplate).then(() => {
            setOriginalOpportunityCloseDateTemplate(opportunityCloseDateTemplate)
            console.log('✅ Opportunity close date template saved')
          })
        )
      }
      
      // Save Account Link Field if configured
      if (accountLinkField) {
        savePromises.push(
          fieldMappingService.setAccountLinkField(accountLinkField).then(() => {
            console.log('✅ Account link field saved')
          })
        )
      }

      await Promise.all(savePromises)

      setSuccess('✅ All General configuration saved successfully!')
      setTimeout(() => setSuccess(null), 3000)
      
      console.log('✅ All General configurations saved')
    } catch (err) {
      console.error('❌ Error saving General configuration:', err)
      setError(err instanceof Error ? err.message : 'Failed to save General configuration')
    } finally {
      setSaving(false)
    }
  }

  // Render General tab content
  const renderGeneralTabContent = () => (
    <div className="general-tab-content">
      {/* Header for General Tab */}
      <div className="field-mappings-header">
        <h2>General Configuration</h2>
        <p className="field-mappings-description">
          Configure system-wide settings for ISO deal submissions and Salesforce integration.
        </p>
        
        {/* Save Button */}
        <div className="general-tab-actions">
          <button 
            onClick={handleSaveGeneralConfig}
            disabled={saving}
            className="btn btn-primary"
          >
            {saving ? (
              <>
                <span className="spinner"></span>
                Saving Configuration...
              </>
            ) : (
              <>
                💾 Save Configuration
              </>
            )}
          </button>
          
          <button 
            onClick={handleSyncSalesforceFields}
            disabled={syncing}
            className="btn btn-info"
          >
            {syncing ? (
              <>
                <span className="spinner"></span>
                Syncing...
              </>
            ) : (
              <>
                🔄 Sync Salesforce Fields
              </>
            )}
          </button>
        </div>
      </div>

      {/* System Configuration Section */}
      <div className="system-configuration-section">
        <div className="config-card">
          <div className="config-header">
            <h3>🔗 System Configuration</h3>
            <p className="config-description">
              Configure how ISO submissions are linked to Salesforce records.
            </p>
          </div>
          
          <div className="config-content">
            <div className="config-field">
              <label className="config-label">
                Account Link Field
                <span className="config-help">Opportunity field to store Account ID</span>
              </label>
              
              <div className="config-input-group">
                <select
                  value={accountLinkField || ''}
                  onChange={(e) => {
                    const fieldName = e.target.value
                    setAccountLinkField(fieldName)
                    if (fieldName) {
                      handleAccountLinkFieldSave(fieldName)
                    }
                  }}
                  className="config-select"
                  disabled={savingConfig || !opportunityFieldsWithReference?.length}
                >
                  <option value="">Select Opportunity field...</option>
                  {opportunityFieldsWithReference?.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label || field.name} ({field.type})
                      {field.isCustom && ' - Custom'}
                    </option>
                  ))}
                </select>
                
                {savingConfig && (
                  <div className="config-saving">
                    <span className="spinner"></span>
                    Saving...
                  </div>
                )}
              </div>
              
              <div className="config-status">
                {accountLinkFieldConfigured ? (
                  <span className="status-configured">
                    ✅ Configured: {accountLinkField}
                  </span>
                ) : (
                  <span className="status-not-configured">
                    ⚠️ Not configured - Account ID linking disabled
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Defaults Configuration Section */}
      <div className="system-configuration-section">
        <div className="config-card">
          <div className="config-header">
            <h3>🎯 Opportunity Defaults</h3>
            <p className="config-description">
              Configure default values and templates for automatically created Opportunity records.
            </p>
          </div>
          
          <div className="config-content">
            {/* Opportunity Name Template */}
            <div className="config-field">
              <label className="config-label">
                Opportunity Name Template
                <span className="config-help">Template for generating opportunity names</span>
              </label>
              
              <div className="config-input-group">
                <input
                  type="text"
                  value={opportunityNameTemplate}
                  onChange={(e) => setOpportunityNameTemplate(e.target.value)}
                  onBlur={(e) => {
                    console.log('🔍 Name template onBlur:', {
                      currentValue: e.target.value,
                      originalValue: originalOpportunityNameTemplate,
                      willSave: e.target.value !== originalOpportunityNameTemplate
                    })
                    if (e.target.value !== originalOpportunityNameTemplate) {
                      handleOpportunityConfigSave('nameTemplate', e.target.value)
                    }
                  }}
                  className="config-input"
                  placeholder="e.g., {businessLegalName} - New - {currentDate}"
                  disabled={savingOpportunityConfig}
                />
                
                {savingOpportunityConfig && (
                  <div className="config-saving">
                    <span className="spinner"></span>
                    Saving...
                  </div>
                )}
              </div>
              
              <div className="config-help-text">
                Available placeholders: {Object.keys(templatePlaceholders).map(key => `{${key}}`).join(', ')}
              </div>
            </div>

            {/* Default Stage */}
            <div className="config-field">
              <label className="config-label">
                Default Stage
                <span className="config-help">Default stage for new opportunities (from Salesforce)</span>
              </label>
              
              <div className="config-input-group">
                <select
                  value={opportunityDefaultStage}
                  onChange={(e) => {
                    const newStage = e.target.value
                    setOpportunityDefaultStage(newStage)
                    handleOpportunityConfigSave('defaultStage', newStage)
                  }}
                  className="config-select"
                  disabled={savingOpportunityConfig || loadingStageValues}
                >
                  {stagePicklistValues.length === 0 ? (
                    <option value="">Loading stages from Salesforce...</option>
                  ) : (
                    <>
                      <option value="">Select a stage...</option>
                      {stagePicklistValues.map(stage => (
                        <option key={stage.value} value={stage.value}>
                          {stage.label}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                
                {(savingOpportunityConfig || loadingStageValues) && (
                  <div className="config-saving">
                    <span className="spinner"></span>
                    {loadingStageValues ? 'Loading...' : 'Saving...'}
                  </div>
                )}
              </div>
              
              <div className="config-help-text">
                {stagePicklistValues.length > 0 
                  ? `${stagePicklistValues.length} stages loaded from your Salesforce org`
                  : 'Loading stage options from Salesforce...'
                }
              </div>
            </div>

            {/* Close Date - Days from Now */}
            <div className="config-field">
              <label className="config-label">
                Close Date (Days from Today)
                <span className="config-help">Number of days to add to today's date</span>
              </label>
              
              <div className="config-input-group">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={opportunityCloseDays}
                  onChange={(e) => {
                    const days = parseInt(e.target.value, 10)
                    if (!isNaN(days) && days >= 1 && days <= 365) {
                      setOpportunityCloseDays(days)
                      handleOpportunityConfigSave('closeDays', days)
                    }
                  }}
                  className="config-input"
                  disabled={savingOpportunityConfig}
                />
                <span className="config-unit">days</span>
              </div>
            </div>

            {/* Close Date Template (Advanced) */}
            <div className="config-field">
              <label className="config-label">
                Close Date Template (Advanced)
                <span className="config-help">Template for dynamic close date calculation</span>
              </label>
              
              <div className="config-input-group">
                <input
                  type="text"
                  value={opportunityCloseDateTemplate}
                  onChange={(e) => setOpportunityCloseDateTemplate(e.target.value)}
                  onBlur={(e) => {
                    console.log('🔍 Close date template onBlur:', {
                      currentValue: e.target.value,
                      originalValue: originalOpportunityCloseDateTemplate,
                      willSave: e.target.value !== originalOpportunityCloseDateTemplate
                    })
                    if (e.target.value !== originalOpportunityCloseDateTemplate) {
                      handleOpportunityConfigSave('closeDateTemplate', e.target.value)
                    }
                  }}
                  className="config-input"
                  placeholder="e.g., {currentDate+30} or {businessStartDate+90}"
                  disabled={savingOpportunityConfig}
                />
              </div>
              
              <div className="config-help-text">
                Use {'{currentDate+X}'} where X is days to add, or leave blank to use days setting above
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Owner Configuration Section */}
      <div className="system-configuration-section">
        <div className="config-card">
          <div className="config-header">
            <h3>👥 Opportunity Owner Assignment</h3>
            <p className="config-description">
              Configure how the OwnerId field is set for new Opportunity records.
            </p>
          </div>
          
          <div className="config-content">
            {/* Field Selection */}
            <div className="config-field">
              <label className="config-label">
                Opportunity Owner Field
                <span className="config-help">Select which Opportunity field to map the owner to</span>
              </label>
              
              <div className="config-input-group">
                <select
                  value={opportunityOwnerFieldName}
                  onChange={(e) => {
                    const fieldName = e.target.value
                    setOpportunityOwnerFieldName(fieldName)
                    if (fieldName) {
                      handleOpportunityOwnerConfigSave(fieldName)
                    }
                  }}
                  className="config-select"
                  disabled={savingOwnerConfig || opportunityReferenceFields.length === 0}
                >
                  <option value="">Select an Opportunity field...</option>
                  {opportunityReferenceFields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label || field.name} ({field.type})
                      {field.referenceTo && ` → ${field.referenceTo.join(', ')}`}
                    </option>
                  ))}
                </select>
                
                {savingOwnerConfig && (
                  <div className="config-saving">
                    <span className="spinner"></span>
                    Saving...
                  </div>
                )}
              </div>
              
              {opportunityReferenceFields.length === 0 && (
                <div className="config-help-text text-warning">
                  ⚠️ No reference fields found. Please sync Salesforce fields first.
                </div>
              )}
              
              {opportunityReferenceFields.length > 0 && (
                <div className="config-help-text">
                  {opportunityReferenceFields.length} reference fields available (filtered for User references)
                </div>
              )}
            </div>

            {/* Owner Type Selection - Only show if field is selected */}
            {opportunityOwnerFieldName && (
              <div className="config-field">
                <label className="config-label">
                  Owner Assignment Type
                  <span className="config-help">Choose how to assign values to {opportunityOwnerFieldName}</span>
                </label>
              
              <div className="config-input-group">
                <div className="radio-group">
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="ownerType"
                      value="dynamic"
                      checked={opportunityOwnerType === 'dynamic'}
                      onChange={(e) => {
                        setOpportunityOwnerType('dynamic')
                        handleOpportunityOwnerConfigSave(undefined, 'dynamic')
                      }}
                      disabled={savingOwnerConfig}
                    />
                    <span className="radio-label">
                      <strong>Dynamic</strong> - Use underwriting owner from ISO approval
                    </span>
                    <small className="radio-help">
                      Uses the underwriting owner selected during ISO invitation approval
                    </small>
                  </label>
                  
                  <label className="radio-option">
                    <input
                      type="radio"
                      name="ownerType"
                      value="static"
                      checked={opportunityOwnerType === 'static'}
                      onChange={(e) => {
                        setOpportunityOwnerType('static')
                        // Load Salesforce users when switching to static
                        if (salesforceUsers.length === 0) {
                          loadSalesforceUsers()
                        }
                      }}
                      disabled={savingOwnerConfig}
                    />
                    <span className="radio-label">
                      <strong>Static</strong> - Use a specific Salesforce user for all opportunities
                    </span>
                    <small className="radio-help">
                      All opportunities will be assigned to the same owner
                    </small>
                  </label>
                </div>
              </div>

            {/* Static Owner Selection */}
            {opportunityOwnerType === 'static' && (
              <div className="config-field">
                <label className="config-label">
                  Default Owner
                  <span className="config-help">Salesforce user to assign as owner</span>
                </label>
                
                <div className="config-input-group">
                  {loadingSalesforceUsers ? (
                    <div className="loading-state">
                      <span className="spinner"></span>
                      Loading Salesforce users...
                    </div>
                  ) : (
                    <select
                      value={opportunityOwnerStaticValue}
                      onChange={(e) => {
                        const userId = e.target.value
                        setOpportunityOwnerStaticValue(userId)
                        handleOpportunityOwnerConfigSave(undefined, 'static', userId)
                      }}
                      className="config-select"
                      disabled={savingOwnerConfig || salesforceUsers.length === 0}
                    >
                      <option value="">Select a Salesforce user...</option>
                      {salesforceUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {salesforceUsers.length === 0 && !loadingSalesforceUsers && (
                    <button
                      onClick={loadSalesforceUsers}
                      className="btn btn-secondary btn-sm"
                      disabled={loadingSalesforceUsers}
                    >
                      Load Salesforce Users
                    </button>
                  )}
                  
                  {savingOwnerConfig && (
                    <div className="config-saving">
                      <span className="spinner"></span>
                      Saving...
                    </div>
                  )}
                </div>
                
                {salesforceUsers.length === 0 && !loadingSalesforceUsers && (
                  <div className="config-help-text text-warning">
                    ⚠️ No Salesforce users loaded. Click "Load Salesforce Users" to fetch available users.
                  </div>
                )}
                
                {salesforceUsers.length > 0 && (
                  <div className="config-help-text">
                    {salesforceUsers.length} Salesforce users available
                  </div>
                )}
              </div>
            )}

            {/* Current Configuration Status */}
            <div className="config-status">
              {!opportunityOwnerFieldName ? (
                <span className="status-not-configured">
                  ⚠️ No field selected - please choose an Opportunity field to map
                </span>
              ) : opportunityOwnerType === 'dynamic' ? (
                <span className="status-configured">
                  ✅ Dynamic assignment to {opportunityOwnerFieldName} - using underwriting owners from approvals
                </span>
              ) : opportunityOwnerStaticValue ? (
                <span className="status-configured">
                  ✅ Static assignment to {opportunityOwnerFieldName}: {salesforceUsers.find(u => u.id === opportunityOwnerStaticValue)?.name || opportunityOwnerStaticValue}
                </span>
              ) : (
                <span className="status-not-configured">
                  ⚠️ Static assignment selected but no user chosen
                </span>
              )}
            </div>
          </div>
        )}

          </div>
        </div>
      </div>
    </div>
  )

  // Early return for loading state  
  if (loading) {
    return (
      <div className="field-mappings-loading">
        <div className="loading-spinner"></div>
        <h5>Loading Field Mappings...</h5>
        <p>Retrieving Salesforce field metadata and existing mappings...</p>
      </div>
    )
  }

  return (
    <div className="field-mappings-container">
      {/* Sidebar */}
      <div className="field-mappings-sidebar">
        <FieldMappingsSideNav
          categories={categories}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          mappingStats={calculateMappingStats()}
        />
      </div>

      {/* Main Content */}
      <div className="field-mappings-content">
        {/* Header */}
        <div className="field-mappings-header">
          <h2>ISO Application Field Mappings</h2>
          <p className="field-mappings-description">
            Configure how form fields map to Salesforce objects for ISO deal submissions.
          </p>

          {/* Alerts */}
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">❌</span>
              {error}
              <button className="alert-close" onClick={() => setError(null)}>×</button>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span className="alert-icon">✅</span>
              {success}
              <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
            </div>
          )}

          {/* Search */}
          <div className="field-mappings-search">
            <input
              type="text"
              placeholder="Search fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Conditional Content Based on Active Category */}
        {activeCategory === 'General' ? renderGeneralTabContent() : (
          <>
            {/* Field Mappings Actions */}
            <div className="field-mappings-actions">
              <button 
                onClick={handleSaveMappings}
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? (
                  <>
                    <span className="spinner"></span>
                    Saving Mappings...
                  </>
                ) : (
                  <>
                    💾 Save Mappings
                  </>
                )}
              </button>
              
              <button 
                onClick={handleSyncSalesforceFields}
                disabled={syncing}
                className="btn btn-info"
              >
                {syncing ? (
                  <>
                    <span className="spinner"></span>
                    Syncing...
                  </>
                ) : (
                  <>
                    🔄 Sync Salesforce Fields
                  </>
                )}
              </button>
            </div>

            {/* Field Mappings Count */}
            <div className="field-mappings-count">
              <span className="mappings-count-text">
                Showing {getFilteredMappings().length} of {mappings.filter(m => m.formFieldCategory === activeCategory).length} field mappings for {activeCategory}
              </span>
            </div>

            {/* Field Mappings Table */}
            <div className="field-mappings-table">
              <div className="field-mappings-table-header">
                <div className="column-form-field">Form Field</div>
                <div className="column-salesforce-object">Salesforce Object</div>
                <div className="column-salesforce-field">Salesforce Field</div>
                <div className="column-validators">Validators</div>
                <div className="column-field-types">Field Types</div>
                <div className="column-required">Required</div>
                <div className="column-status">Status</div>
              </div>

              <div className="field-mappings-table-body">
                {getFilteredMappings().map((mapping) => (
                  <div 
                    key={mapping.formFieldName} 
                    className={`field-mapping-row ${mapping.salesforceField ? 'mapped' : 'unmapped'}`}
                  >
                    <div className="column-form-field">
                      <div className="form-field-info">
                        <strong>{mapping.formFieldLabel}</strong>
                        <small>{mapping.formFieldName}</small>
                        <span className="field-type">{mapping.formFieldType}</span>
                      </div>
                    </div>

                    <div className="column-salesforce-object">
                      <select
                        value={mapping.salesforceObject}
                        onChange={(e) => handleMappingChange(mapping.formFieldName, 'salesforceObject', e.target.value)}
                        className="salesforce-object-select"
                      >
                        <option value="">Select Object...</option>
                        {salesforceObjects.map(obj => (
                          <option key={obj.value} value={obj.value}>
                            {obj.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="column-salesforce-field">
                      <select
                        value={mapping.salesforceField}
                        onChange={(e) => {
                          handleMappingChange(mapping.formFieldName, 'salesforceField', e.target.value)
                          // Auto-save the mapping when salesforce field changes
                          autoSaveMapping(mapping.formFieldName, 'salesforceField', e.target.value, mapping)
                        }}
                        className="salesforce-field-select"
                        disabled={!mapping.salesforceObject}
                      >
                        <option value="">Select Field...</option>
                        {mapping.salesforceObject && salesforceFields[mapping.salesforceObject as keyof SalesforceFieldsCache]?.map(field => (
                          <option key={field.name} value={field.name}>
                            {field.label || field.name} - {field.type}
                          </option>
                        ))}
                      </select>

                      {(() => {
                        // Get field metadata and show picklist count
                        const sfFieldMetadata = getSalesforceFieldMetadata(mapping.salesforceObject, mapping.salesforceField)
                        const picklistOptions = mapping.salesforcePicklistOptions || sfFieldMetadata?.picklistValues
                        
                        return picklistOptions && picklistOptions.length > 0 ? (
                          <div className="picklist-indicator">
                            📋 {picklistOptions.length} options
                          </div>
                        ) : null
                      })()}
                    </div>

                    <div className="column-validators">
                      <div className="validators-section">
                        <div className="validators-dropdown">
                          <select 
                            onChange={(e) => {
                              if (e.target.value) {
                                handleValidatorChange(mapping.formFieldName, e.target.value, true)
                                e.target.value = '' // Reset dropdown
                              }
                            }}
                            className="validator-select"
                          >
                            <option value="">Add Validator...</option>
                            {availableValidators
                              .filter(validator => {
                                const currentValidators = mapping.validationRules?.validators || []
                                return !currentValidators.includes(validator.id)
                              })
                              .map(validator => (
                                <option key={validator.id} value={validator.id}>
                                  {validator.name}
                                </option>
                              ))
                            }
                          </select>
                        </div>
                        
                        <div className="assigned-validators">
                          {(mapping.validationRules?.validators || []).map(validatorId => {
                            const validator = availableValidators.find(v => v.id === validatorId)
                            return validator ? (
                              <span key={validatorId} className="validator-badge">
                                {validator.name}
                                <button 
                                  onClick={() => handleValidatorChange(mapping.formFieldName, validatorId, false)}
                                  className="remove-validator"
                                  title="Remove validator"
                                >
                                  ×
                                </button>
                              </span>
                            ) : null
                          })}
                        </div>
                      </div>
                    </div>

                    <div className="column-field-types">
                      <div className="field-types-info">
                        {(() => {
                          // Get field type from the live Salesforce data or stored mapping
                          const sfFieldMetadata = getSalesforceFieldMetadata(mapping.salesforceObject, mapping.salesforceField)
                          const salesforceFieldType = mapping.salesforceFieldType || sfFieldMetadata?.type
                          
                          // Debug logging for text fields specifically
                          if (!salesforceFieldType && mapping.salesforceField) {
                            console.log(`🔍 Missing SF type for ${mapping.formFieldName}:`, {
                              stored: mapping.salesforceFieldType,
                              live: sfFieldMetadata?.type,
                              field: mapping.salesforceField,
                              object: mapping.salesforceObject
                            })
                          }

                          return salesforceFieldType ? (
                            <div className="salesforce-type">
                              <span className="type-label">SF:</span>
                              <span className={`type-badge sf-type-${salesforceFieldType}`}>
                                {salesforceFieldType}
                              </span>
                            </div>
                          ) : mapping.salesforceField ? (
                            <div className="salesforce-type">
                              <span className="type-label">SF:</span>
                              <span className="type-badge sf-type-loading">Loading...</span>
                            </div>
                          ) : null
                        })()}
                      </div>
                    </div>

                    <div className="column-required">
                      <input
                        type="checkbox"
                        checked={mapping.isRequired}
                        onChange={(e) => handleMappingChange(mapping.formFieldName, 'isRequired', e.target.checked)}
                        className="required-checkbox"
                      />
                    </div>

                    <div className="column-status">
                      <div className="mapping-status">
                        {mapping.salesforceField ? (
                          <span className="status-badge mapped">
                            ✅ Mapped
                          </span>
                        ) : (
                          <span className="status-badge unmapped">
                            ⚠️ Unmapped
                          </span>
                        )}
                        
                        {mapping.fieldMappingValidated && (
                          <span className="status-badge validated">
                            ✓ Validated
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Test Results Modal */}
      {showTestResults && testResults && (
        <TestMappingsModal
          results={testResults}
          onClose={() => setShowTestResults(false)}
        />
      )}
    </div>
  )
}

export default FieldMappingsManager